import React, { useEffect, useState } from "react";
import { Briefcase, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

let _companiesPromise = null;
function loadBusinessCompanies(companyId) {
  if (!_companiesPromise) _companiesPromise = apiClient.get("/my/companies").then((r) => r.data || []).catch(() => { _companiesPromise = null; return []; });
  return _companiesPromise.then((list) => list.filter((c) => c._id !== companyId && c.company_type !== "privat" && !c.archived).map((c) => ({ id: c._id, name: c.name })));
}

/** Privat-Buchhaltung: «Doch Firmenaufwand?» → Beleg zur Firma, als Spesen des Inhabers (Rückerstattung) – ein Klick. */
export function ReclaimBusinessAction({ companyId, item, receipt, companies, onDone, compact = false }) {
  const [targets, setTargets] = useState(companies || null);
  const [busy, setBusy] = useState(null);
  const [pending, setPending] = useState(null); // {target_company_id, target_company_name, item_id, employees}
  const [sel, setSel] = useState("");
  const id = item?.id || receipt?._id;
  const booked = !!receipt && (receipt.status === "booked" || !!receipt.journal_entry_id);

  useEffect(() => { if (companies) setTargets(companies); }, [companies]);
  useEffect(() => { if (targets?.length && !sel) setSel(targets[0].id); }, [targets, sel]);
  useEffect(() => { if (!companies) loadBusinessCompanies(companyId).then(setTargets); }, [companies, companyId]);

  const finish = (r, employeeName) => {
    const n = r.applied_to_pending || 0;
    toast.success(`Beleg → «${r.target_company_name}» als Spesen von ${employeeName} – Aufwand an 2060, Rückerstattung im nächsten Lohnlauf`, {
      duration: 8000,
      description: r.card_saved ? `Karte …${r.card_saved.last4} dort als Privatkarte gemerkt – künftige Belege in der Firma werden automatisch als Spesen erkannt${n ? ` (${n} offene Belege angepasst)` : ""}` : undefined,
    });
    setPending(null);
    onDone?.(r);
  };

  const reclaim = async (t) => {
    if (receipt && !window.confirm(`«${receipt.description || "Beleg"}» ist doch Firmenaufwand?\n\n${booked ? "Die private Buchung wird storniert und der " : "Der "}Beleg geht an «${t.name}» und wird dort als Spesen (Rückerstattung an dich) erfasst.`)) return;
    setBusy(t.id);
    try {
      const url = receipt ? `/companies/${companyId}/receipts/${id}/reclaim-business` : `/companies/${companyId}/inbox/${id}/reclaim-business`;
      const r = (await apiClient.post(url, { target_company_id: t.id })).data;
      if (r.needs_employee) {
        setPending(r);
        toast.info(`Beleg ist bei «${r.target_company_name}» – wem soll die Rückerstattung gutgeschrieben werden?`);
        if (!r.employees?.length) toast.warning("In der Firma sind keine Mitarbeitenden erfasst – Spesen bitte dort unter Lohn → Mitarbeitende anlegen");
      } else finish(r, r.expense_claim?.employee_name);
    } catch (e) { toast.error(e.response?.data?.detail || "Übergabe an die Firma fehlgeschlagen"); }
    finally { setBusy(null); }
  };

  const pickEmployee = async (emp) => {
    setBusy(emp.id);
    try {
      const r = (await apiClient.post(`/companies/${pending.target_company_id}/inbox/${pending.item_id}/expense-claim`, { employee_id: emp.id })).data;
      finish({ ...pending, card_saved: r.card_saved, applied_to_pending: r.applied_to_pending }, emp.name);
    } catch (e) { toast.error(e.response?.data?.detail || "Spesen konnten nicht gesetzt werden"); }
    finally { setBusy(null); }
  };

  if (!targets || targets.length === 0) return null;
  if (pending) {
    return (
      <div className="rounded border border-teal-300 bg-white p-2 text-xs space-y-1.5" data-testid={`reclaim-employee-picker-${id}`}>
        <div className="font-medium text-teal-900">Rückerstattung an wen? (Mitarbeitende von «{pending.target_company_name}»)</div>
        <div className="flex flex-wrap gap-1.5">
          {pending.employees.map((e) => (
            <button key={e.id} type="button" disabled={!!busy} onClick={() => pickEmployee(e)} className="rounded-full border border-teal-300 bg-teal-50 px-2.5 py-1 text-teal-900 hover:bg-teal-100" data-testid={`reclaim-employee-${e.id}`}>
              {busy === e.id ? <Loader2 className="h-3 w-3 animate-spin inline" /> : e.name}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => { setPending(null); onDone?.(pending); }} className="text-slate-500 hover:underline" data-testid={`reclaim-employee-skip-${id}`}>Später in der Firma zuordnen</button>
      </div>
    );
  }
  const many = targets.length > 3;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${compact ? "text-[11px]" : "text-xs"}`} data-testid={`reclaim-business-${id}`}>
      <span className="inline-flex items-center gap-1 text-slate-600"><Briefcase className="h-3.5 w-3.5" /> Doch Firmenaufwand?</span>
      {many && (
        <>
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="rounded border border-teal-300 bg-white px-1.5 py-0.5 text-slate-800" data-testid={`reclaim-business-select-${id}`}>
            {targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button type="button" disabled={!!busy} onClick={() => reclaim(targets.find((t) => t.id === sel) || targets[0])}
            className="inline-flex items-center gap-1 rounded border border-teal-400 bg-teal-600 px-2 py-0.5 font-medium text-white hover:bg-teal-700 disabled:opacity-60" data-testid={`reclaim-business-go-${id}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Als Spesen übergeben (Rückerstattung an mich)
          </button>
        </>
      )}
      {!many && targets.map((t) => (
        <button key={t.id} type="button" disabled={!!busy} onClick={() => reclaim(t)}
          className="inline-flex items-center gap-1 rounded border border-teal-400 bg-teal-600 px-2 py-0.5 font-medium text-white hover:bg-teal-700 disabled:opacity-60"
          title={`Beleg an «${t.name}» übergeben und dort als Spesen (Rückerstattung an dich) erfassen${booked ? " – private Buchung wird storniert" : ""}`}
          data-testid={`reclaim-business-${id}-${t.id}`}>
          {busy === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : null} → «{t.name}» als Spesen (Rückerstattung)
        </button>
      ))}
    </div>
  );
}
